import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type OrderStatus = "pending" | "reviewed" | "cancelled";

// ── Layer 1: primitives ──────────────────────────────────────────────────────
// No React state. These are the single source of truth for the wire-level
// order operations consumed by both the per-order hook and the dashboard's
// batch handlers.

export async function apiSetOrderStatus(orderId: number, newStatus: OrderStatus): Promise<void> {
  const res = await apiRequest(
    "PATCH",
    `/api/orders/${orderId}/status?suppressNotifications=true`,
    { status: newStatus }
  );
  if (!res.ok) {
    throw new Error(`Failed to update status for order ${orderId}`);
  }
}

export async function apiDeleteOrder(orderId: number): Promise<void> {
  const res = await apiRequest("DELETE", `/api/orders/${orderId}`);
  if (!res.ok) {
    throw new Error(`Failed to delete order ${orderId}`);
  }
}

/**
 * Opens a blank window synchronously (so the open isn't blocked as a
 * non-user-gesture popup), fetches the packing-slip PDF for the given order
 * ids, and redirects the window to the resulting blob URL. Falls back to an
 * anchor click if the window failed to open.
 */
export async function openPickSheetInNewWindow(orderIds: number[]): Promise<void> {
  if (orderIds.length === 0) return;
  const placeholder = orderIds.length > 1 ? "Generating pick-sheets..." : "Generating pick-sheet...";
  const pdfWindow = window.open("about:blank", "_blank");
  if (pdfWindow) {
    pdfWindow.document.write(
      `<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif;color:#555"><p>${placeholder}</p></body></html>`
    );
  }
  try {
    const response = await apiRequest("POST", "/api/orders/export-packing-slips-pdf", { orderIds });
    if (!response.ok) {
      if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
      throw new Error("Failed to generate PDF");
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    if (pdfWindow) {
      pdfWindow.location.href = url;
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.click();
    }
  } catch (error) {
    if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
    throw error;
  }
}

// ── Layer 2: per-order hook ──────────────────────────────────────────────────
// TanStack mutations wrapping the primitives, with toasts and query
// invalidations. Does NOT mutate selection state (selectedOrderIds) — destroy
// (delete) is delegated via onRequestDelete so the caller owns its confirm
// dialog.

interface UseOrderActionsOptions {
  orderId: number;
  /**
   * Delegate the send-to-QuickBooks flow back to the parent (dashboard owns
   * the broken-link / unlinked-account / resend-confirm dialogs). Optional —
   * consumers without QB access can omit it.
   */
  onSendToQuickBooks?: (orderId: number, confirmedResend?: boolean) => void | Promise<void>;
  /**
   * Delegate the delete confirmation to the parent (which owns the
   * AlertDialog). Optional — consumers without delete access can omit it.
   */
  onRequestDelete?: (orderIds: number[]) => void;
}

export function useOrderActions({ orderId, onSendToQuickBooks, onRequestDelete }: UseOrderActionsOptions) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const setStatusMutation = useMutation({
    mutationFn: async (newStatus: OrderStatus) => {
      await apiSetOrderStatus(orderId, newStatus);
      return newStatus;
    },
    onSuccess: (newStatus) => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      toast({
        title: "Order status updated",
        description: `Order #ORD-${orderId.toString().padStart(3, "0")} status changed to ${newStatus}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update status",
        description: error?.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const printPickSheetMutation = useMutation({
    mutationFn: async () => {
      await openPickSheetInNewWindow([orderId]);
    },
    onError: () => {
      toast({
        title: "Export failed",
        description: "Failed to generate pick-sheet. Please try again.",
        variant: "destructive",
      });
    },
  });

  return {
    setStatus: (status: OrderStatus) => setStatusMutation.mutate(status),
    setStatusAsync: (status: OrderStatus) => setStatusMutation.mutateAsync(status),
    cancelOrder: () => setStatusMutation.mutate("cancelled"),
    sendToQuickBooks: (confirmedResend = false) => {
      if (onSendToQuickBooks) return onSendToQuickBooks(orderId, confirmedResend);
    },
    printPickSheet: () => printPickSheetMutation.mutate(),
    requestDelete: () => onRequestDelete?.([orderId]),

    isSettingStatus: setStatusMutation.isPending,
    isPrintingPickSheet: printPickSheetMutation.isPending,
  };
}
